import { useState } from "react";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import { postContact } from "@/lib/api";
import { Mail, MapPin, Globe2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const INQUIRY_TYPES = [
  { id: "general", label: "General" },
  { id: "partnership", label: "Partnership" },
  { id: "media", label: "Media" },
  { id: "speaking", label: "Speaking" },
  { id: "consultancy", label: "Consultancy" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", inquiry_type: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postContact(form);
      toast.success("Thank you. The Institute will respond within three working days.");
      setForm({ name: "", email: "", organisation: "", inquiry_type: "general", message: "" });
    } catch {
      toast.error("Could not send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact & partnerships"
        title="Write to the Institute."
        lead="For institutional enquiries, partnership proposals, media requests, speaking engagements, conference participation or consultancy. We read every message."
      />

      <section className="container-x section-y border-t border-border">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-10">
            <div>
              <SectionEyebrow>Direct</SectionEyebrow>
              <div className="mt-6 space-y-4">
                <ContactRow icon={Mail} label="institute@cleanability.org" testId="contact-email" />
                <ContactRow icon={MapPin} label="London · Singapore · Accra" testId="contact-location" />
                <ContactRow icon={Globe2} label="32 countries · 68 partner institutions" testId="contact-reach" />
              </div>
            </div>

            <div>
              <SectionEyebrow>What we respond to</SectionEyebrow>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Institutional partnership proposals",
                  "Healthcare and infrastructure pilots",
                  "Press, journals and broadcast",
                  "Conference and keynote requests",
                  "Consultancy and standards work",
                ].map((l, i) => (
                  <li key={l} data-testid={`respond-${i}`} className="flex items-start gap-2">
                    <ArrowUpRight className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> {l}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 rounded-3xl border border-border bg-card p-8 md:p-10 space-y-5">
            <div className="flex flex-wrap gap-2" data-testid="contact-types">
              {INQUIRY_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm({ ...form, inquiry_type: t.id })}
                  data-testid={`contact-type-${t.id}`}
                  className={`px-3.5 h-9 rounded-md text-xs font-medium border transition-colors ${
                    form.inquiry_type === t.id
                      ? "bg-foreground text-background border-foreground"
                      : "border-border bg-background text-foreground/80 hover:border-[hsl(var(--accent))]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormInput label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-name" />
              <FormInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email-input" />
              <div className="md:col-span-2">
                <FormInput label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testId="contact-org" />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono-mini text-muted-foreground">Your message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                required
                className="mt-2 w-full px-4 py-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send message"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 text-sm">
      <span className="h-9 w-9 rounded-md border border-border bg-card grid place-items-center">
        <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
      </span>
      {label}
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
